import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { LANG_STORAGE_KEY } from '@shared/services/usuario/language.service';

const SUPPORTED = ['es', 'en', 'pt'];
/** Mismas claves que usa StorefrontLocaleService: idioma elegido por el comprador y el aplicado en la pestaña. */
const STOREFRONT_LANG_KEY = 'cs_storefront_lang';
const STOREFRONT_ACTIVE_LANG_KEY = 'cs_storefront_lang_active';

/**
 * #FUNCIONALIDAD Añade `Accept-Language` con el idioma que el usuario está viendo en ESTA pestaña, para
 * que el backend traduzca mensajes y exportaciones (CSV, Excel, PDF) igual que la pantalla. Antes se leía
 * de localStorage, que comparten todas las pestañas: si en otra se cambiaba a inglés, esta seguía en
 * español pero sus exportaciones salían en inglés. localStorage queda solo como respaldo (antes de que
 * Transloco tenga idioma) y por defecto «es».
 */
export const languageInterceptor: HttpInterceptorFn = (req, next) => {
  // Los JSON de traducción los pide el propio Transloco: no se le inyecta aquí (dependencia circular).
  if (req.url.includes('assets/i18n/')) return next(req);
  // Quien pide un idioma concreto lo indica (p. ej. el panel leyendo los métodos de pago de la tienda).
  if (req.headers.has('Accept-Language')) return next(req);
  // La tienda pública tiene su propio idioma (el del comprador), distinto del panel.
  const lang = req.url.includes('/api/storefront') ? storefrontLang() : resolveLang(activeTransloco());
  return next(req.clone({ setHeaders: { 'Accept-Language': lang } }));
};

/**
 * Idioma que muestra la tienda en esta pestaña (es/en/pt): el aplicado, aunque venga del idioma por defecto de la
 * tienda y el comprador no haya tocado el selector (B-14); si no, el guardado; por defecto «es».
 */
function storefrontLang(): string {
  try {
    const active = sessionStorage.getItem(STOREFRONT_ACTIVE_LANG_KEY);
    if (active === 'es' || active === 'en' || active === 'pt') return active;
    const stored = localStorage.getItem(STOREFRONT_LANG_KEY);
    if (stored === 'es' || stored === 'en' || stored === 'pt') return stored;
  } catch {
    /* localStorage no disponible */
  }
  return 'es';
}

function activeTransloco(): TranslocoService | null {
  try {
    return inject(TranslocoService, { optional: true });
  } catch {
    return null;
  }
}

function resolveLang(transloco: TranslocoService | null): string {
  const active = transloco?.getActiveLang();
  if (active && SUPPORTED.includes(active)) return active;
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch {
    /* localStorage no disponible: usar el idioma por defecto */
  }
  return 'es';
}
